import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, Sprout, TrendingUp, BadgeCheck, Sparkles, Download } from "lucide-react";
import { useEffect, useState } from "react";
import LogoMark from "../components/LogoMark";
import EarningsCalculator from "../components/EarningsCalculator";

export default function Landing() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    if (window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const installApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice?.outcome === "accepted") setInstalled(true);
    setInstallPrompt(null);
  };

  return (
    <div className="min-h-screen bg-[#F8F4E9] text-[#1F1816]">
      {/* Top bar */}
      <header className="border-b border-[#E8E0D2] bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark size="md" />
          </Link>
          <div className="flex items-center gap-2">
            {installPrompt && !installed && (
              <button onClick={installApp} data-testid="install-app-btn"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm border border-[#E8E0D2] hover:border-[#D71921] hover:text-[#D71921] bg-white">
                <Download className="w-4 h-4" /> Install App
              </button>
            )}
            <Link to="/admin/login" data-testid="landing-admin-link"
              className="hidden md:inline-flex px-4 py-2 text-sm text-[#6B5F58] hover:text-[#1F1816]">
              Admin Console
            </Link>
            <Link to="/login" data-testid="landing-login-btn"
              className="px-4 py-2 rounded-xl text-sm border border-[#E8E0D2] text-[#1F1816] hover:bg-white">
              Sign in
            </Link>
            <Link to="/register" data-testid="landing-register-btn"
              className="px-4 py-2 rounded-xl text-sm text-white font-medium shadow-lg shadow-[#D71921]/25"
              style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)" }}>
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* decorative blobs */}
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#D71921]/10 blur-3xl pointer-events-none" />
        <div className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#B8862A]/15 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-72 h-72 rounded-full bg-[#3F6B3F]/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 border"
              style={{ background: "linear-gradient(90deg, #FCEDED 0%, #FFF5DA 100%)", borderColor: "#F2C97A", color: "#7A1010" }} data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5" />
              Earn passive income from livestock
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Own a flock.<br />
              <span className="italic text-[#D71921]">Earn while it grows.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#6B5F58] leading-relaxed max-w-xl">
              Buy a curated lot of sheep & goats. We feed, vet and raise them. After the lock-in we sell at market and share the profits with you. Every animal is tagged, weighed, and tracked &mdash; you watch your investment grow in real time.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register" data-testid="cta-register"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-medium hover:-translate-y-0.5 transition-all shadow-lg shadow-[#D71921]/25"
                style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)" }}>
                Start earning <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="cta-login"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white border-2 border-[#E8E0D2] text-[#1F1816] font-medium hover:border-[#D71921]">
                I have an account
              </Link>
              {installPrompt && !installed && (
                <button onClick={installApp} className="sm:hidden inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-medium"
                  style={{ background: "linear-gradient(135deg, #B8862A 0%, #8E6517 100%)" }}>
                  <Download className="w-4 h-4" /> Install app
                </button>
              )}
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                ["3-4mo", "young stock", "#D71921"],
                ["2 mo", "lock-in", "#B8862A"],
                ["~25%", "avg returns", "#3F6B3F"],
              ].map(([k, v, c]) => (
                <div key={k} className="bg-white/60 backdrop-blur rounded-xl p-3 border border-[#E8E0D2]">
                  <div className="font-display text-2xl" style={{ color: c }}>{k}</div>
                  <div className="text-xs text-[#6B5F58] mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative animate-in delay-2">
            {/* gold halo */}
            <div className="absolute inset-0 -m-6 rounded-[2rem] bg-gradient-to-br from-[#F2C97A]/40 to-[#D71921]/20 blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-2xl shadow-[#D71921]/15 aspect-[4/5]">
              <img alt="Sheep grazing" data-testid="hero-image"
                src="https://images.unsplash.com/photo-1768333005088-4ebfb737faed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxzaGVlcCUyMGZhcm0lMjBncmVlbiUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NzcxNzg3ODR8MA&ixlib=rb-4.1.0&q=85"
                className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-6 md:p-8 text-white">
                <div className="text-xs uppercase tracking-widest text-[#F2C97A] font-bold">Lot LOT-0007</div>
                <div className="font-display text-2xl mt-1">Highland Pasture</div>
                <div className="text-sm opacity-90 mt-1">12 goats &middot; 3 months &middot; ₹1,20,000</div>
              </div>
            </div>
            {/* floating ai card */}
            <div className="absolute -left-4 md:-left-10 bottom-10 bg-white rounded-2xl shadow-2xl shadow-black/10 border border-[#E8E0D2] p-4 md:p-5 max-w-[260px]">
              <div className="flex items-center gap-2 text-[#D71921] text-xs font-semibold uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5" /> AI Health Insight
              </div>
              <p className="text-sm text-[#1F1816] mt-2 leading-snug">
                12 goats showed +18% weight growth this month. Optimal sale window: <b className="text-[#3F6B3F]">March 22</b>.
              </p>
            </div>
            {/* floating profit card */}
            <div className="absolute -right-4 md:-right-8 top-10 rounded-2xl p-4 shadow-2xl border border-[#F2C97A]/40 max-w-[200px]"
              style={{ background: "linear-gradient(135deg, #FFF5DA 0%, #FCEDED 100%)" }}>
              <div className="text-[10px] uppercase tracking-widest text-[#7A1010] font-bold">Avg Profit</div>
              <div className="font-display text-2xl text-[#D71921] mt-1">₹26,400</div>
              <div className="text-xs text-[#6B5F58]">per lot &middot; last quarter</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-[#E8E0D2]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-widest text-[#6B5F58] font-semibold mb-3">How it works</div>
            <h2 className="font-display text-3xl md:text-4xl">A grounded investment, end-to-end transparent.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: BadgeCheck, title: "1. Verify your KYC", body: "Sign up with phone, upload PAN & Aadhaar. Admin reviews within 24 hours." },
              { icon: Sprout, title: "2. Pick a lot, fund it", body: "Each lot is a curated set of 3-month-old goats. Pay securely via Razorpay." },
              { icon: TrendingUp, title: "3. Sell &amp; profit", body: "After 2-month lock-in, request sale. We deduct expenses and pay you the rest." },
            ].map((s, i) => (
              <div key={s.title} className={`gf-card p-7 animate-in delay-${i+1}`}>
                <s.icon className="w-7 h-7 text-[#3F6B3F] mb-4" />
                <div className="font-display text-xl mb-2">{s.title}</div>
                <p className="text-sm text-[#6B5F58] leading-relaxed" dangerouslySetInnerHTML={{__html: s.body}} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Calculator */}
      <EarningsCalculator />

      {/* Footer */}
      <footer className="py-10 text-center text-sm text-[#6B5F58]">
        <div>&copy; 2026 PassiveIearn. Investments subject to livestock-market risk.</div>
      </footer>
    </div>
  );
}
